import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Alert, Badge, Box, Button, Heading, SelectInputNative, Spinner, TextareaInput } from '@hyphen/hyphen-components';

import { api } from '../lib/api.js';
import { useResource } from '../lib/session.js';
import { Artifacts } from './evidence-run.jsx';
import { Comments } from './comments.jsx';
import { History } from './history.jsx';
import { PendingProposal, SubmitProposal } from './proposal.jsx';

// The value a field holds now, under the name the API serves it as — which is
// not the column name a change is made against for the two fields that still
// carry the auditor's original alongside Hyphen's wording.
const SERVED_AS = {
  proposed_control: 'control',
  proposed_drl_t1: 'documentation_request',
};

const storedValue = (record, field) => record[SERVED_AS[field] ?? field];

const FIELDS = [
  { name: 'proposed_control', label: 'Control' },
  { name: 'proposed_drl_t1', label: 'Documentation request' },
  { name: 'drl_t2', label: 'Type 2 evidence request' },
  { name: 'population', label: 'Type 2 population' },
  { name: 'sampling_guidance', label: 'Type 2 sampling guidance' },
  { name: 'notes', label: 'Notes' },
];

// How the control appears on the public trust portal, written for a customer's
// security reviewer rather than the auditor. It is listed only when it has a
// title and a category and is marked listed. The categories are the server's
// list; it refuses anything else.
const PUBLIC_FIELDS = [
  { name: 'public_title', label: 'Public title', rows: 1 },
  { name: 'public_description', label: 'Public description', rows: 2 },
  {
    name: 'public_category',
    label: 'Public category',
    options: [
      'Infrastructure security',
      'Organizational security',
      'Product security',
      'Internal security procedures',
      'Data and privacy',
    ].map((value) => ({ value, label: value })),
  },
  {
    name: 'public_listing',
    label: 'On the public portal',
    options: [{ value: 'listed', label: 'Listed' }],
  },
];

/**
 * One control: editing for those who may edit, proposing for those who may not.
 *
 * Which of the two, or neither, is decided here rather than in each field. A
 * proposal awaiting a decision locks the control for everyone, editors
 * included — the values it was made against must not move under it.
 */
export function ControlDetail({ canEdit, canComment }) {
  const { haiID } = useParams();
  const { data: record, error, setData, setError } = useResource(`/controls/${encodeURIComponent(haiID)}`);
  const { data: proposals, setData: setProposals } = useResource(
    `/controls/${encodeURIComponent(haiID)}/proposals`, [haiID]);
  const [saving, setSaving] = useState(false);
  // What a collaborator has changed and not yet submitted, by field name.
  const [drafts, setDrafts] = useState({});
  // Bumped to throw the field boxes away and build them again from what is
  // stored. Discarding a batch cleared this list and left the text sitting in
  // the boxes, because each box holds what is being typed and nothing was
  // telling it to stop.
  const [generation, setGeneration] = useState(0);
  const discard = () => {
    setDrafts({});
    setGeneration((n) => n + 1);
  };

  async function save(field, value) {
    setSaving(true);
    setError(null);
    try {
      // An empty box means "clear this", which the API expresses as null. The
      // distinction matters: null clears the column, "" would store a blank.
      setData(await api.patch(`/controls/${encodeURIComponent(haiID)}`, {
        fields: { [field]: value === '' ? null : value },
      }));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  if (error && !record) return <Alert variant="danger">{error}</Alert>;
  if (!record) return <Spinner />;

  const pending = (proposals ?? []).find((proposal) => proposal.status === 'pending');
  const mode = pending ? 'read' : canEdit ? 'save' : canComment ? 'draft' : 'read';

  function draft(field, typed) {
    setDrafts((current) => {
      const next = { ...current, [field]: typed === '' ? null : typed };
      // A field typed back to what it already says is not a change.
      if ((next[field] ?? '') === (storedValue(record, field) ?? '')) delete next[field];
      return next;
    });
  }

  function decided(proposal) {
    setProposals((proposals ?? []).map((p) => (p.id === proposal.id ? proposal : p)));
    // An approval changes the control, so read it back rather than guess.
    api.get(`/controls/${encodeURIComponent(haiID)}`).then(setData).catch((err) => setError(err.message));
  }

  return (
    <Box gap="3xl">
      <Link to="/admin/controls">&larr; All controls</Link>

      <Box direction="row" alignItems="center" gap="xl">
        <Heading as="h1" size="lg">{record.hai_id}</Heading>
        <Badge variant="outline">{record.tsp_raw}</Badge>
        {saving && <Spinner size="sm" />}
      </Box>

      {error && <Alert variant="warning">{error}</Alert>}

      {pending && (
        <PendingProposal proposal={pending} canEdit={canEdit} onDecided={decided} onError={setError} />
      )}

      {/*
        Keyed on the control, on when it last changed, and on the discard
        count, so the fields are rebuilt from what is stored whenever that is
        no longer what they are showing. Without the control in the key the
        uncontrolled boxes below persist across controls — type in one,
        navigate to another, and your text follows you — and without the other
        two they keep showing what was typed after a discard, or what was there
        before a proposal was approved.
      */}
      <Box gap="2xl" maxWidth="60rem" key={`${record.hai_id}:${record.updated_at}:${generation}`}>
        {FIELDS.map((field) => (
          <Field
            key={field.name}
            label={field.label}
            name={field.name}
            value={storedValue(record, field.name)}
            mode={mode}
            saving={saving}
            onSave={save}
            onDraft={draft}
          />
        ))}

        <Box gap="xs">
          <Heading as="h2" size="md">On the public trust portal</Heading>
          <Box as="p" fontSize="sm" color="secondary">
            What a customer reads about this control. It is shown only when it has a title and a category
            and is listed; the wording above never is.
          </Box>
        </Box>
        {PUBLIC_FIELDS.map((field) => (
          <Field
            key={field.name}
            label={field.label}
            name={field.name}
            value={storedValue(record, field.name)}
            rows={field.rows}
            options={field.options}
            mode={mode}
            saving={saving}
            onSave={save}
            onDraft={draft}
          />
        ))}
      </Box>

      {mode === 'draft' && (
        <SubmitProposal
          haiID={record.hai_id}
          changes={drafts}
          onSubmitted={(proposal) => {
            discard();
            setProposals([proposal, ...(proposals ?? [])]);
          }}
          onDiscard={discard}
        />
      )}

      <ControlEvidence haiID={record.hai_id} />

      <Comments haiID={record.hai_id} canComment={canComment} />

      <History haiID={record.hai_id} />
    </Box>
  );
}

/**
 * The most recent capture of each artifact that evidences this control — what
 * currently backs it. Older captures stay on their runs.
 */
function ControlEvidence({ haiID }) {
  const { data: artifacts, error } = useResource(`/controls/${encodeURIComponent(haiID)}/evidence`);

  return (
    <Box gap="xl">
      <Heading as="h2" size="md">Evidence</Heading>
      {error && <Alert variant="warning">{error}</Alert>}
      {!artifacts && !error && <Spinner />}
      {artifacts && artifacts.length === 0 && (
        <Box color="secondary">No collected evidence for this control yet.</Box>
      )}
      {artifacts && artifacts.length > 0 && <Artifacts artifacts={artifacts} showRun />}
    </Box>
  );
}

function Field({ label, name, value, rows = 3, options, mode, saving, onSave, onDraft }) {
  // Uncontrolled: the field holds what the person is typing, and is rebuilt
  // when the record changes because the container above is keyed on it. A
  // controlled input would fight every keystroke against the last saved value.
  const [draft, setDraft] = useState(value ?? '');
  const changed = draft !== (value ?? '');

  if (mode === 'read') {
    const shown = options?.find((option) => option.value === value)?.label ?? value;
    return (
      <Box gap="xs">
        <Box as="span" fontSize="sm" fontWeight="medium">{label}</Box>
        <Box as="p" color={value ? 'base' : 'tertiary'}>{shown || '—'}</Box>
      </Box>
    );
  }

  // A field with a fixed set of values is chosen from them, with "—" for none;
  // choosing one is a change like typing is, saved or proposed the same way.
  const input = (onChange, disabled) =>
    options ? (
      <SelectInputNative
        id={`field-${name}`}
        label={label}
        value={draft}
        options={[{ value: '', label: '—' }, ...options]}
        isDisabled={disabled}
        onChange={onChange}
      />
    ) : (
      <TextareaInput
        label={label}
        name={name}
        value={draft}
        rows={rows}
        isDisabled={disabled}
        helpText={mode === 'draft' && changed ? 'Changed — will be proposed' : undefined}
        onChange={onChange}
      />
    );

  // Proposing: what is typed joins the batch below, submitted with a reason for
  // an editor to decide. Nothing to save here, because this person cannot.
  if (mode === 'draft') {
    return input((event) => {
      setDraft(event.target.value);
      onDraft(name, event.target.value);
    }, false);
  }

  // Saving on blur was invisible and surprising: nothing said an edit had been
  // written, and clicking away mid-thought wrote it anyway. Changes are kept
  // until they are saved deliberately, and Cancel puts the saved text back.
  return (
    <Box gap="sm">
      {input((event) => setDraft(event.target.value), saving)}
      {changed && (
        <Box direction="row" gap="sm" alignItems="center">
          <Button size="sm" isLoading={saving} onClick={() => onSave(name, draft)}>Save</Button>
          <Button size="sm" variant="secondary" isDisabled={saving}
                  onClick={() => setDraft(value ?? '')}>Cancel</Button>
          <Box as="span" fontSize="sm" color="secondary">Unsaved changes</Box>
        </Box>
      )}
    </Box>
  );
}
